import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_Updater,
} from 'material-react-table';
import UserManagementService from '../../../Services/UserManagementService';
import Notification from '../../Layout/Notification';
import TenantDetails from './TenantDetails';
import { useTheme } from '@mui/material/styles';
import './UserManagement.scss';

function extractRows(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (
    data &&
    typeof data === 'object' &&
    'content' in data &&
    Array.isArray((data as { content: unknown[] }).content)
  )
    return (data as { content: unknown[] }).content;
  return [];
}

function extractTotal(response: { data: unknown; headers: Record<string, unknown> }): number {
  const raw = response.headers?.['x-total-count'];
  const headerCount = parseInt(typeof raw === 'string' ? raw : String(raw ?? ''), 10);
  if (!Number.isNaN(headerCount) && headerCount > 0) return headerCount;
  const data = response.data;
  if (data && typeof data === 'object' && !Array.isArray(data) && 'totalElements' in data)
    return (data as { totalElements?: number }).totalElements ?? 0;
  return extractRows(data).length;
}

function filtersToParams(columnFilters: MRT_ColumnFiltersState) {
  const params: Record<string, string> = {};
  for (const { id, value } of columnFilters) {
    const v = String(value ?? '').trim();
    if (!v) continue;
    if (id === 'name') params.name = v;
    else if (id === 'tenantKey') params.tenantKey = v;
  }
  return params;
}

interface TenantsProps {
  onMount?: (actions: {
    add: () => void;
    export: () => void;
    toggleFilters: () => void;
    toggleFullScreen: () => void;
  }) => void;
}

const Tenants = ({ onMount }: TenantsProps) => {
  const theme = useTheme();
  const ACCENT = theme.tokens.accent;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<unknown | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [tenants, setTenants] = useState<unknown[]>([]);
  const [totalTenants, setTotalTenants] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);

  const fetchIdRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const fetchData = useCallback(async () => {
    const id = ++fetchIdRef.current;
    setLoading(true);
    try {
      const filterParams = filtersToParams(columnFilters);
      const response = await UserManagementService.getTenants({
        page,
        size: rowsPerPage,
        sort: `${sortBy},${sortOrder}`,
        name: filterParams.name ?? '',
        tenantKey: filterParams.tenantKey ?? '',
      });
      if (id !== fetchIdRef.current) return;
      setTenants(extractRows(response.data));
      setTotalTenants(extractTotal(response));
    } catch (err: unknown) {
      if (id !== fetchIdRef.current) return;
      Notification.fromError(err, { fallbackTitle: "Couldn't load tenants" });
    } finally {
      if (id === fetchIdRef.current) setLoading(false);
    }
  }, [page, rowsPerPage, sortBy, sortOrder, columnFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = useCallback(async () => {
    setLoading(true);
    try {
      const filterParams = filtersToParams(columnFilters);
      await UserManagementService.exportTenants({
        sort: `${sortBy},${sortOrder}`,
        name: filterParams.name ?? '',
        tenantKey: filterParams.tenantKey ?? '',
      });
    } catch (err: unknown) {
      Notification.fromError(err, { fallbackTitle: 'Export failed' });
    } finally {
      setLoading(false);
    }
  }, [columnFilters, sortBy, sortOrder]);

  const handleViewDetails = (tenant: unknown) => {
    setSelectedTenant(tenant);
    setDialogOpen(true);
  };
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTenant(null);
  };
  const handleAddTenant = useCallback(() => {
    setSelectedTenant(null);
    setDialogOpen(true);
  }, []);

  const handleColumnFiltersChange = useCallback(
    (updaterOrValue: MRT_Updater<MRT_ColumnFiltersState>) => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setColumnFilters(updaterOrValue);
        setPage(0);
      }, 350);
    },
    []
  );

  const columns = useMemo<MRT_ColumnDef<Record<string, unknown>>[]>(
    () => [
      { accessorKey: 'name', header: 'Tenant Name', size: 300, filterVariant: 'text' },
      { accessorKey: 'tenantKey', header: 'Tenant Key', size: 300, filterVariant: 'text' },
    ],
    []
  );

  const sorting = useMemo(() => [{ id: sortBy, desc: sortOrder === 'desc' }], [sortBy, sortOrder]);

  const table = useMaterialReactTable({
    data: tenants as Record<string, unknown>[],
    columns,
    manualSorting: true,
    enableSortingRemoval: false,
    enableMultiSort: false,
    manualPagination: true,
    manualFiltering: true,
    rowCount: totalTenants,
    enableColumnActions: false,
    enableColumnFilters: true,
    enableGlobalFilter: false,
    enableHiding: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableTopToolbar: true,
    enableToolbarInternalActions: false,
    renderTopToolbar: ({ table }) =>
      table.getState().isFullScreen ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '4px 8px',
            backgroundColor: 'var(--t-surface-subtle)',
            borderBottom: '1px solid var(--t-border, rgba(0,0,0,0.12))',
          }}
        >
          <button
            onClick={() => table.setIsFullScreen(false)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: ACCENT,
              fontSize: 13,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
            title="Exit Fullscreen"
          >
            ✕ Exit Fullscreen
          </button>
        </div>
      ) : (
        <div style={{ display: 'none' }} />
      ),
    enableFilterMatchHighlighting: false,
    enableStickyHeader: true,
    columnFilterDisplayMode: 'subheader',
    initialState: { density: 'compact', showColumnFilters: false },
    state: {
      sorting,
      columnFilters,
      pagination: { pageIndex: page, pageSize: rowsPerPage },
      isLoading: loading,
    },
    onColumnFiltersChange: handleColumnFiltersChange,
    onSortingChange: updater => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      if (next[0]) {
        setSortBy(next[0].id);
        setSortOrder(next[0].desc ? 'desc' : 'asc');
      }
    },
    onPaginationChange: updater => {
      const prev = { pageIndex: page, pageSize: rowsPerPage };
      const next = typeof updater === 'function' ? updater(prev) : updater;
      setPage(next.pageIndex);
      setRowsPerPage(next.pageSize);
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => handleViewDetails(row.original),
      sx: { cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(94,51,191,0.04)' } },
    }),
    paginationDisplayMode: 'pages',
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: 2,
        border: '1px solid var(--t-border, rgba(0,0,0,0.12))',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      },
    },
    muiTableContainerProps: {
      sx: { flex: 1, overflow: 'auto' },
    },
    muiTableHeadCellProps: {
      sx: {
        fontWeight: 700,
        fontSize: 13,
        backgroundColor: 'var(--t-surface-subtle)',
        borderBottom: `2px solid ${ACCENT}`,
      },
    },
    muiFilterTextFieldProps: {
      variant: 'outlined',
      size: 'small',
      sx: { '& .MuiInputBase-root': { fontSize: 12, borderRadius: '6px' } },
    },
    muiTableBodyCellProps: { sx: { fontSize: 13 } },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: 'var(--t-surface-subtle)',
        minHeight: 40,
        '& .MuiBox-root': { justifyContent: 'center' },
      },
    },
  });

  // Register actions with parent (after table is created)
  useEffect(() => {
    onMount?.({
      add: handleAddTenant,
      export: handleExport,
      toggleFilters: () => table.setShowColumnFilters((prev: boolean) => !prev),
      toggleFullScreen: () => table.setIsFullScreen((prev: boolean) => !prev),
    });
  }, [onMount, handleAddTenant, handleExport, table]);

  return (
    <div
      id="tenants-container"
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <MaterialReactTable table={table} />
      <TenantDetails
        open={dialogOpen}
        tenant={selectedTenant as Record<string, unknown> | null}
        onClose={handleCloseDialog}
        onUpdateData={fetchData}
      />
    </div>
  );
};

export default Tenants;
